import is from '@sindresorhus/is';

import type {
    CareerFrameworkViewId,
    FrameworkModeId,
    JobArchitectureSidebarId,
    JobProfileViewId,
    PersonalPreferencesSidebarId,
    SubscriptionBillingSidebarId,
} from './constants';

export const companySettingsRoute = {
    params: {
        jobArchitectureId: 'jobArchitectureId',
        jobProfileView: 'jobProfileView',
    },
    patterns: {
        entry: '/companySettings',
        jobArchitecture: '/companySettings/job-architecture/:jobArchitectureId?',
        organization: '/companySettings/organization',
        personalPreferences: '/companySettings/personal-preferences',
        subscriptionBilling: '/companySettings/subscription-billing',
    },
    searchParams: {
        careerFrameworkView: 'careerFrameworkView',
        frameworkId: 'frameworkId',
        frameworkMode: 'frameworkMode',
        jobProfileView: 'jobProfileView',
        openCareerFrameworkEdit: 'openCareerFrameworkEdit',
        openInvite: 'openInvite',
        returnUrl: 'returnUrl',
        sidebar: 'sidebar',
    },
};

export type JobArchitectureSettingsOptions = {
    readonly careerFrameworkView?: CareerFrameworkViewId;
    readonly frameworkId?: string;
    readonly frameworkMode?: FrameworkModeId;
    readonly jobProfileView?: JobProfileViewId;
    readonly openCareerFrameworkEdit?: boolean;
    readonly sidebar?: JobArchitectureSidebarId;
};

export type OrganizationSettingsUrlOptions = {
    readonly openInvite?: boolean;
    readonly sidebar?: string;
};

export const buildOrganizationSettingsUrl = (
    sidebarItem?: string,
    options?: OrganizationSettingsUrlOptions,
): string => {
    const params = new URLSearchParams();

    const sidebar = options?.sidebar ?? sidebarItem;
    if (!is.undefined(sidebar)) {
        params.set(companySettingsRoute.searchParams.sidebar, sidebar);
    }

    if (options?.openInvite === true) {
        params.set(companySettingsRoute.searchParams.openInvite, '1');
    }

    const queryString = params.toString();

    return is.emptyString(queryString)
        ? companySettingsRoute.patterns.organization
        : `${companySettingsRoute.patterns.organization}?${queryString}`;
};

export const buildJobArchitectureSettingsUrl = (
    jobArchitectureId?: string,
    options?: JobArchitectureSettingsOptions,
): string => {
    const basePattern = companySettingsRoute.patterns.jobArchitecture;
    const baseUrl = is.undefined(jobArchitectureId)
        ? basePattern.replace('/:jobArchitectureId?', '')
        : basePattern.replace(':jobArchitectureId?', jobArchitectureId);

    const url = is.undefined(options?.jobProfileView) ? baseUrl : `${baseUrl}/job-profile/${options.jobProfileView}`;

    const params = new URLSearchParams();

    if (!is.undefined(options?.sidebar)) {
        params.set(companySettingsRoute.searchParams.sidebar, options.sidebar);
    }

    if (!is.undefined(options?.careerFrameworkView)) {
        params.set(companySettingsRoute.searchParams.careerFrameworkView, options.careerFrameworkView);
    }

    if (!is.undefined(options?.frameworkId)) {
        params.set(companySettingsRoute.searchParams.frameworkId, options.frameworkId);
    }

    if (!is.undefined(options?.frameworkMode)) {
        params.set(companySettingsRoute.searchParams.frameworkMode, options.frameworkMode);
    }

    if (options?.openCareerFrameworkEdit === true) {
        params.set(companySettingsRoute.searchParams.openCareerFrameworkEdit, 'true');
    }

    const queryString = params.toString();

    return is.emptyString(queryString) ? url : `${url}?${queryString}`;
};

export const buildPersonalPreferencesSettingsUrl = (sidebarItem?: PersonalPreferencesSidebarId): string => {
    const params = new URLSearchParams();

    if (!is.undefined(sidebarItem)) {
        params.set(companySettingsRoute.searchParams.sidebar, sidebarItem);
    }

    const queryString = params.toString();

    return is.emptyString(queryString)
        ? companySettingsRoute.patterns.personalPreferences
        : `${companySettingsRoute.patterns.personalPreferences}?${queryString}`;
};

export const buildSubscriptionBillingSettingsUrl = (sidebarItem?: SubscriptionBillingSidebarId): string => {
    const params = new URLSearchParams();

    if (!is.undefined(sidebarItem)) {
        params.set(companySettingsRoute.searchParams.sidebar, sidebarItem);
    }

    const queryString = params.toString();

    return is.emptyString(queryString)
        ? companySettingsRoute.patterns.subscriptionBilling
        : `${companySettingsRoute.patterns.subscriptionBilling}?${queryString}`;
};
